import { useEffect, useState } from "react";
import { get, type Asset, type Member, type Trip } from "../lib/api.ts";
import { dayQuery, localDayOf } from "../lib/days.ts";
import { reason } from "../ui/index.ts";
import { MediaPopup } from "./media/MediaPopup.tsx";

/** The viewer's own zone decides which day a recording belongs to, as everywhere else. */
const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * One recording, opened from its card on the map.
 *
 * The map holds a read model — a place, a time, a kind — and nothing else, because that is all a
 * map needs. Everything the popup shows beyond that (components, tags, comments,
 * the grants for a preview and the original) belongs to the recording itself, so it is fetched
 * the moment a card is opened rather than for every pin on screen.
 *
 * Exactly the pager the Media tab uses, with nothing added. It used to be handed a line about
 * where the place came from; the recording now carries its own place and says it by name, so
 * arriving from the map and arriving from the library show the same thing.
 *
 * What it pages through is the day the pin belongs to, read in one go: a map is a picture of a
 * day, and the recording beside this one is the one taken next, wherever on the map it was. Each
 * card the reader lands on moves the map's clock to the moment it was taken, so the pins, the
 * travelled line and the needle underneath are all telling the same time as the picture on top.
 */
export function MapCardSheet({
  trip,
  assetId,
  members,
  onClose,
  onActive,
}: {
  trip: Trip;
  assetId: string;
  members: Member[];
  onClose: () => void;
  /** The recording now being read, so the map can move its clock to the moment it was taken. */
  onActive?: (asset: Asset) => void;
}) {
  const [asset, setAsset] = useState<Asset | null>(null);
  /** That recording's whole day, which is what paging sideways moves through. */
  const [day, setDay] = useState<Asset[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    setAsset(null);
    setError(null);
    get<Asset>(`/v1/trips/${trip.id}/assets/${assetId}`)
      .then((a) => {
        if (live) setAsset(a);
      })
      .catch((e: Error) => {
        if (live) setError(reason(e));
      });
    return () => {
      live = false;
    };
  }, [trip.id, assetId, nonce]);

  // The day around it, read to the end as the library reads one. Until it lands the pager holds
  // the single recording that was opened, so the card is never empty while the day is in flight.
  const openedAt = asset?.captureAt ?? null;
  useEffect(() => {
    if (openedAt === null) return;
    let live = true;
    void (async () => {
      const key = localDayOf(Date.parse(openedAt), TIME_ZONE);
      const page: Asset[] = [];
      let cursor: string | undefined;
      try {
        do {
          const q = dayQuery(key, TIME_ZONE, cursor);
          const r = await get<{ items: Asset[]; nextCursor: string | null }>(
            `/v1/trips/${trip.id}/assets?${q}`,
          );
          page.push(...r.items);
          cursor = r.nextCursor ?? undefined;
        } while (cursor);
      } catch {
        // The one recording is already in hand; a day that would not load is no reason to
        // refuse to show it.
        return;
      }
      if (live && page.some((a) => a.id === assetId)) setDay(page);
    })();
    return () => {
      live = false;
    };
  }, [trip.id, assetId, openedAt]);

  if (error) {
    return (
      <div className="sheet-scrim" onClick={onClose}>
        <div className="filter-panel" role="alertdialog" onClick={(e) => e.stopPropagation()}>
          <p role="alert" className="error">
            {error}
          </p>
          <div className="row wrap">
            <button type="button" className="button" onClick={() => setNonce((n) => n + 1)}>
              Try again
            </button>
            <button type="button" className="button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Nothing is drawn until the recording is in hand: a popup that appears empty and fills in
  // reads as broken on a phone, where it covers the map it was opened from.
  if (!asset) return null;

  return (
    <MediaPopup
      trip={trip}
      items={day.length > 0 ? day : [asset]}
      startId={asset.id}
      members={members}
      {...(onActive ? { onActive } : {})}
      onClose={onClose}
      onChanged={() => setNonce((n) => n + 1)}
    />
  );
}
